import { minimumFootprintDistanceFeet } from './pushGeometry';
import { weaponReachFeet } from './weaponReach';
import { getCleaveCreatureSize } from './weaponMasteryCleave';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { HORDE_BREAKER_FEATURE_ID } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';

const combatantId = (participant) => String(participant?.characterId || participant?.enemyId || participant?.combatantId || participant?.id || '');

/** Longest range of a ranged or thrown attack, from "ammunition (150/600)"-style properties or range fields. */
export const weaponLongRangeFeet = (weapon = {}) => {
  const texts = [...(Array.isArray(weapon?.properties) ? weapon.properties : []).map((property) => String(property?.name || property)), String(weapon?.range || '')];
  const ranges = texts.map((text) => /(\d+)\s*\/\s*(\d+)/.exec(text)).filter(Boolean).map((match) => Number(match[2]));
  const fields = [weapon?.longRange, weapon?.normalRange].map(Number).filter((value) => Number.isFinite(value) && value > 0);
  return Math.max(0, ...ranges, ...fields);
};

const currentLedger = (combatState, attackerId) => {
  const ledger = combatState?.turnActionLedger;
  return ledger?.combatantId === String(attackerId) && ledger?.turnSequence === combatState?.turnSequence ? ledger : null;
};

/**
 * Creatures a Horde Breaker attack may target: within 5 feet of the original target, within the
 * weapon's range (reach for melee, long range for ranged), and not attacked this turn.
 */
export const getHordeBreakerCandidateIds = ({ attackerId, primaryTargetId, weapon, isMeleeAttack, combatState, spatialContext } = {}) => {
  const map = spatialContext?.map;
  const tokens = spatialContext?.tokens;
  const sourceToken = tokens?.[attackerId];
  const primaryToken = tokens?.[primaryTargetId];
  if (!map || !sourceToken || !primaryToken) return [];
  const range = isMeleeAttack ? weaponReachFeet(weapon) : weaponLongRangeFeet(weapon);
  if (!range) return [];
  const attacked = new Set(currentLedger(combatState, attackerId)?.attackedTargetIds || []);
  const participants = Array.isArray(combatState?.participants) ? combatState.participants : [];
  const byId = new Map(participants.map((entry) => [combatantId(entry), entry]));
  const size = (id, token) => getCleaveCreatureSize({ id, participant: byId.get(String(id)), token, entities: spatialContext?.entities });
  return participants.map(combatantId).filter((candidateId) => {
    if (!candidateId || [String(attackerId), String(primaryTargetId)].includes(candidateId) || attacked.has(candidateId)) return false;
    const participant = byId.get(candidateId);
    if (participant?.defeated === true || Number(participant?.currentHp) === 0 || Number(spatialContext?.entities?.[candidateId]?.currentHp) === 0) return false;
    const candidateToken = tokens[candidateId];
    if (!candidateToken) return false;
    const nearPrimary = minimumFootprintDistanceFeet({ sourceToken: primaryToken, sourceSize: size(primaryTargetId, primaryToken), targetToken: candidateToken, targetSize: size(candidateId, candidateToken), map });
    const fromAttacker = minimumFootprintDistanceFeet({ sourceToken, sourceSize: size(attackerId, sourceToken), targetToken: candidateToken, targetSize: size(candidateId, candidateToken), map });
    return nearPrimary != null && nearPrimary <= 5 && fromAttacker != null && fromAttacker <= range;
  });
};

/** Horde Breaker follows any weapon attack (hit or miss) on the Hunter's turn, once per turn. */
export const qualifiesForHordeBreaker = ({ character, attack, outcome, combatState, attackerId, candidateIds } = {}) => {
  const ledger = currentLedger(combatState, attackerId);
  return hasSubclassFeature(character, HORDE_BREAKER_FEATURE_ID) && attack?.kind === 'weapon' &&
    ['hit', 'critical-hit', 'miss'].includes(outcome) && Boolean(ledger) && !ledger.hordeBreaker?.spent &&
    Array.isArray(candidateIds) && candidateIds.length > 0;
};

export const createHordeBreakerDecision = (resolutionId) => ({
  id: `${resolutionId}:${HORDE_BREAKER_FEATURE_ID}`, featureId: HORDE_BREAKER_FEATURE_ID, resolutionId,
  label: "Hunter's Prey: Horde Breaker",
  prompt: 'Attack a different creature within 5 feet of your target with the same weapon? Once per turn; it uses no attack from your Action.',
  choices: [{ id: 'apply', label: 'Horde Breaker' }, { id: 'skip', label: 'Skip' }],
});
